/**
 * Application Resource Hooks
 *
 * Type-safe hooks for application-related API operations.
 */

import { useQuery } from '@tanstack/react-query';
import type { paths } from '@access-cms/api-types';
import { ResourceType } from '@access-cms/shared-constants';

import { ApiRequestError, getApiClientConfig } from '../../client';
import { queryKeys } from '../../queryKeys';
import type { QueryParamRecord } from '../../types';
import { useApiCreate, useApiUpdate, useApiUpsert } from '../useApiMutation';
import { useApiGet, useApiList } from '../useApiQuery';

// =============================================================================
// Type Aliases
// =============================================================================

type ApplicationListResponse = paths['/api/v1/applications']['get']['responses']['200']['content']['application/json'];
type ApplicationResponse = paths['/api/v1/applications/{id}']['get']['responses']['200']['content']['application/json'];
type ApplicationCreateBody = paths['/api/v1/applications']['post']['requestBody']['content']['application/json'];
type ApplicationUpdateBody = NonNullable<
  paths['/api/v1/applications/{id}']['patch']['requestBody']
>['content']['application/json'];

type Application = NonNullable<ApplicationResponse['data']>;
type ApplicationListFilters = paths['/api/v1/applications']['get']['parameters']['query'];

// =============================================================================
// Query Hooks
// =============================================================================

interface UseApplicationsQueryOptions {
  enabled?: boolean;
  filters?: ApplicationListFilters;
}

/**
 * Fetch a paginated list of applications
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useApplicationsQuery({
 *   filters: { status: 'PENDING', limit: 20 }
 * });
 * ```
 */
export function useApplicationsQuery(options?: UseApplicationsQueryOptions) {
  const { filters, enabled } = options ?? {};

  return useApiList('/api/v1/applications', {
    enabled,
    queryKey: queryKeys[ResourceType.APPLICATIONS].list(filters as QueryParamRecord),
    queryParams: filters,
  });
}

interface UseApplicationQueryOptions {
  enabled?: boolean;
  include?: string[];
  /**
   * Request the computed eNOVA workflow progress, which also surfaces
   * `returnFeedback` (reviewer/COR return comments) at the top level of the
   * response for applications in a returned status. Reviewers/CORs need this
   * to see the clarifications a VAS return-to-COR carries (ACP-1027).
   */
  includeWorkflowProgress?: boolean;
  /**
   * Poll while the record is waiting on something the caller cannot be notified about —
   * an FNMS visit id landing after the submission reaches FNMS (ACP-2752). Pass false, or
   * leave it unset, for the normal fetch-once behaviour.
   */
  refetchInterval?: number | false;
}

/**
 * Fetch a single application by ID
 *
 * @example
 * ```tsx
 * const { data: application, isLoading } = useApplicationQuery(applicationId);
 * ```
 */
export function useApplicationQuery(id: string, options?: UseApplicationQueryOptions) {
  const queryParams: QueryParamRecord = {};
  if (options?.include) queryParams.include = options.include;
  if (options?.includeWorkflowProgress) queryParams.includeWorkflowProgress = true;

  return useApiGet(
    '/api/v1/applications/{id}',
    { id },
    {
      enabled: options?.enabled ?? !!id,
      queryKey: queryKeys[ResourceType.APPLICATIONS].detail(
        id,
        Object.keys(queryParams).length > 0 ? queryParams : undefined,
      ),
      queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
      refetchInterval: options?.refetchInterval ?? false,
    },
  );
}

/**
 * Fetch application history
 */
export function useApplicationHistoryQuery(id: string, options?: UseApplicationQueryOptions) {
  return useApiList('/api/v1/applications/{id}/history', {
    enabled: options?.enabled ?? !!id,
    pathParams: { id },
    queryKey: queryKeys[ResourceType.APPLICATIONS].history(id),
  });
}

/**
 * Fetch the fine-grained, role-scoped application timeline (commit-log-style lifecycle
 * events, including the per-attempt FNMS submission events). Server-side, the FN approver
 * only sees events up to and including the FNMS stage (ACP-247, C4).
 */
export function useApplicationTimelineQuery(id: string, options?: UseApplicationQueryOptions) {
  return useApiList('/api/v1/applications/{applicationId}/timeline', {
    enabled: options?.enabled ?? !!id,
    pathParams: { applicationId: id },
    queryKey: queryKeys[ResourceType.APPLICATIONS].timeline(id),
  });
}

/**
 * The four Foreign National Information answers plus their audit/lock metadata.
 * The OpenAPI response for this endpoint is loosely typed (`data: Record`), so this
 * shape is declared here to match the entity-manager contract.
 */
interface ForeignNationalInfo {
  applicationId?: string;
  cmsOrganization?: string | null;
  logicalAccess?: string | null;
  informationAccess?: string | null;
  informationSystems?: string | null;
  complete?: boolean;
  authoredByUserId?: string | null;
  authoredAt?: string | null;
  lastUpdatedByUserId?: string | null;
  lastUpdatedAt?: string | null;
  lockedAt?: string | null;
}

/** The four answers a COR may write. */
type ForeignNationalInfoInput = Pick<
  ForeignNationalInfo,
  'cmsOrganization' | 'logicalAccess' | 'informationAccess' | 'informationSystems'
>;

/**
 * Fetch the Foreign National Information for an application.
 *
 * Authorized for the COR (during review), the FN approver, and admins; the applicant
 * is denied server-side. Only Foreign National applications carry a record.
 */
export function useForeignNationalInfoQuery(id: string, options?: UseApplicationQueryOptions) {
  return useApiGet(
    '/api/v1/applications/{id}/foreign-national-information',
    { id },
    {
      enabled: options?.enabled ?? !!id,
      queryKey: queryKeys[ResourceType.APPLICATIONS].foreignNationalInfo(id),
    },
  );
}

interface UseUpsertForeignNationalInfoOptions {
  applicationId: string;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

/**
 * Create or update the Foreign National Information answers (COR, during review).
 *
 * Publishes `application.fn_info.saved` (and `.completed` once all four are present)
 * server-side. Rejected with `409.EM.FN.4005` once the record is locked on FN approval.
 *
 * @example
 * ```tsx
 * const saveFnInfo = useUpsertForeignNationalInfo({ applicationId });
 * saveFnInfo.mutate({ body: { cmsOrganization: '...', logicalAccess: '...' } });
 * ```
 */
export function useUpsertForeignNationalInfo(options: UseUpsertForeignNationalInfoOptions) {
  return useApiUpsert({
    invalidateKeys: [queryKeys[ResourceType.APPLICATIONS].foreignNationalInfo(options.applicationId)],
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/applications/{id}/foreign-national-information',
    pathParams: { id: options.applicationId },
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

interface UseCreateApplicationOptions {
  onError?: (error: Error) => void;
  onSuccess?: (response: ApplicationResponse) => void;
}

/**
 * Create a new application
 */
export function useCreateApplication(options?: UseCreateApplicationOptions) {
  return useApiCreate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/applications',
  });
}

interface UseUpdateApplicationOptions {
  onError?: (error: Error) => void;
  onSuccess?: (response: ApplicationResponse) => void;
}

/**
 * Update an existing application
 */
export function useUpdateApplication(options?: UseUpdateApplicationOptions) {
  return useApiUpdate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/applications/{id}',
  });
}

/**
 * Submit an application for review
 */
interface UseSubmitApplicationOptions {
  onError?: (error: Error) => void;
  onSuccess?: () => void;
  pathParams?: { id: string };
}

export function useSubmitApplication(options?: UseSubmitApplicationOptions) {
  return useApiCreate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/applications/{id}/submit',
    pathParams: options?.pathParams,
  });
}

/**
 * Submit a review decision for an application
 */
export function useReviewApplication(options?: { onError?: (error: Error) => void; onSuccess?: () => void }) {
  return useApiCreate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/applications/{id}/review',
  });
}

/**
 * Withdraw an application
 */
export function useWithdrawApplication(options?: { onError?: (error: Error) => void; onSuccess?: () => void }) {
  return useApiCreate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/applications/{id}/withdraw',
  });
}

type ApplicationStatusActionResponse =
  paths['/api/v1/applications/{id}/status']['post']['responses']['200']['content']['application/json'];

interface UseApplicationStatusActionOptions {
  applicationId: string;
  onError?: (error: Error) => void;
  onSuccess?: (response: ApplicationStatusActionResponse) => void;
}

/**
 * Perform a status action on an application (APPROVE, REJECT, RETURN, etc.)
 *
 * @example
 * ```tsx
 * const statusAction = useApplicationStatusAction({
 *   applicationId,
 *   onSuccess: () => toast.success('Application approved'),
 * });
 *
 * statusAction.mutate({ action: 'APPROVE' });
 * ```
 */
export function useApplicationStatusAction(options: UseApplicationStatusActionOptions) {
  return useApiCreate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/applications/{id}/status',
    pathParams: { id: options.applicationId },
  });
}

type FnmsRetryResponse =
  paths['/api/v1/applications/{id}/fnms-retry']['post']['responses']['200']['content']['application/json'];

interface UseFnmsRetryOptions {
  applicationId: string;
  onError?: (error: Error) => void;
  onSuccess?: (response: FnmsRetryResponse) => void;
}

/**
 * Retry a parked FNMS submission (ACP-2421).
 *
 * Only valid while the application sits in `FNMS_SUBMISSION_FAILED`; the server replays the
 * dead-lettered job rather than rebuilding the submission, and answers 409 from any other
 * status. Restricted to ADMINISTRATOR and FOREIGN_NATIONAL.
 *
 * @example
 * ```tsx
 * const retry = useFnmsRetry({ applicationId, onSuccess: () => refetch() });
 * retry.mutate({});
 * ```
 */
export function useFnmsRetry(options: UseFnmsRetryOptions) {
  return useApiCreate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/applications/{id}/fnms-retry',
    pathParams: { id: options.applicationId },
  });
}

// =============================================================================
// Application Initialization (GET /api/v1/applications/me)
// =============================================================================

/** Top-level reason: only present when the user has no candidate contracts at all. */
type MyApplicationsReason = 'NO_ROSTER_MEMBERSHIP';

/** Per-contract reason: entry has no application because the roster row lacks a labor category. */
type ContractApplicationReason = 'MISSING_LABOR_CATEGORY';

interface ApplicationInitializationApp {
  applicationData: Record<string, unknown>;
  applicationStatus: string;
  /** Fingerprints/eApp region tracking (db-core ApplicationRegions). The UI
   * does not depend on db-core, so the shape is redeclared here. */
  applicationSubStatus?: { fingerprints?: string; eapp?: string; version?: number } | null;
  applicationType: string;
  contractId: string;
  createdAt: string;
  currentStep: number;
  id: string;
  laborCategoryId: string | null;
  updatedAt: string | null;
  userId: string;
}

/**
 * One candidate contract's entry in the /applications/me map. `application` is
 * the user's in-flight or APPROVED application on that contract when one
 * exists (or the auto-created DRAFT when this is the sole candidate); when
 * null, `reason` explains why no application could be created.
 */
interface ContractApplicationEntry {
  application: ApplicationInitializationApp | null;
  contract: Record<string, unknown> | null;
  laborCategory: { id: string; categoryName?: string } | null;
  reason?: ContractApplicationReason;
}

interface MyApplicationsData {
  /** One entry per candidate contract, keyed by contractId. */
  applications: Record<string, ContractApplicationEntry>;
  reason?: MyApplicationsReason;
}

interface MyApplicationsResponse {
  data: MyApplicationsData;
  meta: {
    message: string;
    requestId: string;
    status: number;
    timestamp: string;
  };
}

/**
 * Pick the entry the UI should act on: the entry for an explicit contractId
 * when given, otherwise the sole entry when exactly one candidate exists.
 * Returns undefined when the user must pick between multiple contracts.
 */
export function selectContractApplication(
  data: MyApplicationsData | undefined,
  contractId?: string | null,
): ContractApplicationEntry | undefined {
  if (contractId) {
    return data?.applications[contractId];
  }
  const entries = Object.values(data?.applications ?? {});

  return entries.length === 1 ? entries[0] : undefined;
}

/**
 * Fetch the user's candidate contracts via GET /api/v1/applications/me.
 *
 * Returns a map keyed by contractId with one entry per candidate contract
 * (a contract where the user has an in-flight or APPROVED application, or an
 * eligible roster membership on a contract not already approved):
 * - Exactly one candidate → the server auto-creates a DRAFT when needed
 *   (get-or-create), so the sole entry carries the application
 * - Multiple candidates → data only; the user must pick a contract
 * - No candidates → `applications` is empty and `reason` is set
 */
interface UseMyApplicationsQueryOptions {
  enabled?: boolean;
}

export function useMyApplicationsQuery(options?: UseMyApplicationsQueryOptions) {
  return useQuery<MyApplicationsResponse, ApiRequestError, MyApplicationsData>({
    enabled: options?.enabled ?? true,
    queryFn: async ({ signal }) => {
      const config = getApiClientConfig();
      const url = `${config.baseUrl}/api/v1/applications/me`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20_000);
      signal.addEventListener('abort', () => controller.abort());

      try {
        const response = await fetch(url, {
          credentials: 'include',
          headers: {
            ...config.defaultHeaders,
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
        });

        const data = await response.json();

        if (!response.ok) {
          throw ApiRequestError.fromResponse(data, response.status);
        }

        return data as MyApplicationsResponse;
      } finally {
        clearTimeout(timeoutId);
      }
    },
    queryKey: queryKeys[ResourceType.APPLICATIONS].me(),
    retry: 0,
    select: (response) => response.data,
  });
}

// =============================================================================
// COR Field Update
// =============================================================================

/**
 * Update COR-owned fields on an application
 *
 * Only COR- or ADMIN-role users can call this. Only contractDetails fields
 * are accepted. Uses merge-patch semantics — omitted fields are unchanged.
 *
 * @example
 * ```tsx
 * const updateCorFields = useCorFieldUpdate(applicationId);
 *
 * updateCorFields.mutate({
 *   body: {
 *     contractDetails: {
 *       workEmail: 'cor.a@example.com',
 *       workPhoneNumber: '555-0123',
 *     },
 *   },
 * });
 * ```
 */
export function useCorFieldUpdate(applicationId: string, options?: UseUpdateApplicationOptions) {
  return useApiUpdate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/applications/{id}/cor-fields',
    pathParams: { id: applicationId },
  });
}

// =============================================================================
// Exports
// =============================================================================

export type {
  Application,
  ApplicationCreateBody,
  ApplicationInitializationApp,
  ApplicationListFilters,
  ApplicationListResponse,
  ApplicationResponse,
  ApplicationUpdateBody,
  ContractApplicationEntry,
  ContractApplicationReason,
  ForeignNationalInfo,
  ForeignNationalInfoInput,
  MyApplicationsData,
  MyApplicationsReason,
};
